// frontend/src/components/layout/ChangePasswordModal.tsx
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Check, Eye, EyeOff, KeyRound, Loader2, LogOut, X } from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';

/** Mismas reglas que el servidor (`src/core/users/password.ts`). */
const MIN_LENGTH = 8;
const MAX_LENGTH = 72;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Primer ingreso: no hay forma de cerrar ni cancelar, solo cambiarla o salir de la sesión. */
  forced?: boolean;
}

/**
 * Cambio de la propia contraseña. Pide la actual siempre (también en el primer ingreso:
 * el usuario acaba de recibir la generada). El servidor renueva las cookies de la sesión,
 * así que después del cambio se sigue trabajando sin volver a entrar.
 */
export function ChangePasswordModal({ open, onClose, forced = false }: Props) {
  const markPasswordChanged = useAuthStore((s) => s.markPasswordChanged);
  const logout = useAuthStore((s) => s.logout);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Al abrir se arranca de cero: nada de arrastrar lo tecleado en un intento anterior.
  useEffect(() => {
    if (!open) return;
    setCurrent('');
    setNext('');
    setRepeat('');
    setShow(false);
    setSaving(false);
    setError(null);
    setDone(false);
  }, [open]);

  // El temporizador del aviso de éxito no puede sobrevivir al desmontaje del componente.
  useEffect(() => () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);

  // Escape cierra, salvo en el cambio obligatorio.
  useEffect(() => {
    if (!open || forced) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, forced, onClose]);

  // Al abrir, el foco arranca en el primer campo (en el cambio obligatorio no hay otra cosa
  // que hacer en la pantalla, así que el teclado tiene que caer ahí solo).
  useEffect(() => {
    if (!open) return;
    const primero = dialogRef.current?.querySelector<HTMLInputElement>('#cp-current');
    primero?.focus();
  }, [open]);

  if (!open) return null;

  /**
   * Cambio obligatorio: el foco NO puede salir de esta pantalla con el tabulador
   * (detrás hay una aplicación entera que el usuario todavía no puede usar).
   */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const onDialogKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!forced || e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) return;
    const primero = nodes[0];
    const ultimo = nodes[nodes.length - 1];
    const activo = document.activeElement as HTMLElement | null;
    const adentro = !!activo && root.contains(activo);
    if (e.shiftKey && (!adentro || activo === primero)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && (!adentro || activo === ultimo)) {
      e.preventDefault();
      primero.focus();
    }
  };

  const localError = (): string | null => {
    if (!current) return 'Escribí tu contraseña actual.';
    if (next.length < MIN_LENGTH) return `La contraseña nueva debe tener al menos ${MIN_LENGTH} caracteres.`;
    if (next.length > MAX_LENGTH) return `La contraseña nueva no puede tener más de ${MAX_LENGTH} caracteres.`;
    if (next === current) return 'La contraseña nueva debe ser distinta de la actual.';
    if (next !== repeat) return 'Las dos contraseñas nuevas no coinciden.';
    return null;
  };

  const submit = async () => {
    setError(null);
    const bad = localError();
    if (bad) { setError(bad); return; }

    setSaving(true);
    let res: { success: boolean; error?: string };
    try {
      res = await api.post('/auth/change-password', { current, next });
    } catch {
      setSaving(false);
      setError('No se pudo conectar con el servidor. Probá de nuevo.');
      return;
    }
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo cambiar la contraseña.');
      return;
    }

    // Éxito: se muestra el aviso un instante y recién ahí se cierra (en el cambio
    // obligatorio, marcar el cambio hace desaparecer la pantalla que tapa todo).
    setDone(true);
    setCurrent('');
    setNext('');
    setRepeat('');
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      markPasswordChanged();
      onClose();
    }, 1400);
  };

  const label = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
  const field =
    'w-full px-3 py-2 pr-10 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)] disabled:opacity-60';

  const passwordField = (
    id: string,
    value: string,
    onChange: (v: string) => void,
    autoFocus = false
  ) => (
    <div className="relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        autoFocus={autoFocus}
        autoComplete={id === 'cp-current' ? 'current-password' : 'new-password'}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !saving && !done) submit(); }}
        disabled={saving || done}
        className={field}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        title={show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
        aria-label={show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-title"
      aria-describedby={forced ? 'cp-forced-desc' : undefined}
      onKeyDown={onDialogKeyDown}
      className={`fixed inset-0 flex items-center justify-center p-4 ${
        forced ? 'z-[100] bg-black/70 backdrop-blur-sm' : 'z-[60] bg-black/50'
      }`}
    >
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[var(--color-primary)]" />
            <h3 id="cp-title" className="text-base font-semibold text-[var(--color-text)]">
              Cambiar contraseña
            </h3>
          </div>
          {!forced && (
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {forced && (
          <p id="cp-forced-desc" className="mb-4 text-sm text-[var(--color-text-secondary)]">
            Por seguridad, elegí una contraseña nueva antes de continuar.
          </p>
        )}

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-600">
            {error}
          </div>
        )}

        {done && (
          <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-green-500/10 border border-green-500/30 text-green-600 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Listo, tu contraseña quedó cambiada.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className={label} htmlFor="cp-current">Contraseña actual</label>
            {passwordField('cp-current', current, setCurrent, true)}
          </div>
          <div>
            <label className={label} htmlFor="cp-next">Contraseña nueva</label>
            {passwordField('cp-next', next, setNext)}
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Entre {MIN_LENGTH} y {MAX_LENGTH} caracteres, y distinta de la actual.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="cp-repeat">Repetir la contraseña nueva</label>
            {passwordField('cp-repeat', repeat, setRepeat)}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          {forced ? (
            <button
              onClick={() => logout()}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-error)] hover:bg-[var(--color-surface-hover)] inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={submit}
            disabled={saving || done}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Cambiar contraseña
          </button>
        </div>
      </div>
    </div>
  );
}
